"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useCurrency } from "@/lib/useCurrency";
import {
  formatMoney,
  formatRate,
  getPlanPricing,
  SETUP_FEE,
  type Currency,
} from "@/lib/constants/pricing-display";

/**
 * FAQ answers that mention money are currency-aware: the setup-fee and overage
 * lines are rendered from the two-currency pricing map in the currency chosen
 * by the pricing section's US$ / CA$ toggle (read from the ev_currency cookie).
 */
function buildFaqs(currency: Currency) {
  const setup = `${formatMoney(SETUP_FEE, currency)} ${currency}`;
  const businessOverage = `${formatRate(getPlanPricing(currency, "business").overage)} ${currency}`;
  const enterpriseOverage = `${formatRate(getPlanPricing(currency, "enterprise").overage)} ${currency}`;
  return [
  {
    q: "Who is Ethos Virtual built for?",
    a: "We primarily serve market research firms, survey companies, and call center teams worldwide. Our platform is designed for organizations that need scalable, multilingual voice outreach — whether for respondent recruitment, customer follow-ups, or high-volume calling campaigns.",
  },
  {
    q: "What does Ethos Virtual actually do?",
    a: "We provide AI-powered voice agents that handle automated calling, dialing, follow-ups, reminders, confirmations, lead qualification, research recruitment, and multilingual customer outreach. Our platform lets teams run voice campaigns at scale without building or managing an in-house calling operation.",
  },
  {
    q: "How fast can I go live?",
    a: "Same day in most cases. Every agent is custom-built for your business: we tailor the script, voice, greetings, and call handling to your services and hours, and most clients are taking live calls the same day they sign up.",
  },
  {
    q: "What kinds of calls can AI voice agents handle?",
    a: "Our voice agents can handle outbound dialing, appointment reminders, follow-up calls, lead qualification screening, and survey and research recruitment. They operate across multiple languages and can be configured for a wide range of structured conversations.",
  },
  {
    q: "Can clients sign up directly on the website?",
    a: "Yes. The Small Business, Business, and Enterprise plans are all available for direct subscription through Stripe checkout. You can select your plan, complete payment securely, and begin onboarding without needing to go through a lengthy procurement process.",
  },
  {
    q: "Is there a setup fee?",
    a: `Small Business plans (Starter, Standard, and High-Volume) have no setup fee. You subscribe and your AI receptionist is configured at no extra cost. Business and Enterprise plans include a one-time ${setup} setup fee that covers hands-on campaign configuration and launch support.`,
  },
  {
    q: "Can we pay annually instead of monthly?",
    a: "Yes. Every plan offers annual billing at a 10% discount: you pay for the year up front and the effective monthly rate drops. Use the Monthly / Yearly toggle on the pricing section to compare, or contact us and we will set up annual billing for you.",
  },
  {
    q: "How does pricing work if we go over our included minutes?",
    a: `Each plan includes a set number of monthly voice minutes. If you exceed your included allocation, additional minutes are billed at the overage rate for your plan: ${businessOverage} per minute on the Business Plan and ${enterpriseOverage} per minute on the Enterprise Plan. There are no surprise fees.`,
  },
  {
    q: "Why does the Enterprise plan have a lower minute rate?",
    a: "The Enterprise plan is designed for higher-volume operations. The lower per-minute rate reflects the economies of a larger monthly commitment and is paired with deeper operational support, more included minutes, and dedicated project management. It's structured to reward scale.",
  },
  {
    q: "How quickly can we launch?",
    a: "Most teams are up and running within days of subscribing. After plan selection and onboarding, we help configure your campaign structure, objectives, and call workflows. Enterprise clients receive hands-on project setup from a dedicated project manager.",
  },
  {
    q: "Do you support multilingual campaigns?",
    a: "Yes. The Business Plan supports 10+ languages, and the Enterprise Plan supports 55+ languages. This makes Ethos Virtual well-suited for teams running campaigns across diverse regions, demographics, or international markets.",
  },
  {
    q: "Is there project support included?",
    a: "Both plans include operational support. The Business Plan follows a shared responsibility model where we provide tools and the client manages day-to-day inputs. The Enterprise Plan includes a dedicated project manager with end-to-end oversight, campaign design, optimization, and direct reporting.",
  },
  {
    q: "Can this be used for research recruitment, reminders, or customer follow-ups?",
    a: "Absolutely. These are among the most common use cases for Ethos Virtual. Whether you're recruiting survey respondents, reminding clients of upcoming appointments, or following up after a service interaction, our voice agents handle these workflows reliably and at scale.",
  },
  {
    q: "Can your system fit into our current workflow?",
    a: "Yes. Ethos Virtual is built to integrate with existing CRMs, scheduling tools, and reporting environments. We work with your team during onboarding to ensure the platform fits naturally into your current operations without forcing a complete overhaul.",
  },
  ];
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { currency } = useCurrency();
  const faqs = buildFaqs(currency);

  return (
    <section id="faqs" className="section-padding" style={{ background: "var(--color-bg)" }}>
      <div className="section-inner" style={{ maxWidth: 780 }}>
        <h2 className="section-heading">
          Frequently asked <em style={{ fontFamily: "var(--font-heading)" }}>questions</em>
        </h2>
        <p className="section-subheading">
          Honest answers to the questions teams ask most before getting started.
        </p>

        <div>
          {faqs.map((faq, i) => (
            <div className="faq-item" key={i}>
              <button
                className="faq-question"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
                aria-controls={`faq-answer-${i}`}
              >
                <span>{faq.q}</span>
                <motion.span
                  animate={{ rotate: openIndex === i ? 180 : 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ flexShrink: 0, marginLeft: 16 }}
                >
                  <ChevronDown size={18} />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === i && (
                  <motion.div
                    id={`faq-answer-${i}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="faq-answer">{faq.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
